/**
 * kik-variant-compare-card
 *
 * Detects variant changes by observing when the theme's VariantPicker
 * swaps DOM blocks (replaces [data-product-block] elements).
 * Reads the new variant JSON from variant-picker [data-selected-variant-json],
 * then fetches the matching card via the Section Rendering API.
 */
if (!customElements.get('kik-variant-compare-card')) {
    class KikVariantCompareCard extends HTMLElement {
        constructor() {
            super();
        }

        get baseOptionPos() {
            return parseInt(this.getAttribute('data-base-optionpos'), 10);
        }

        get baseOptionValue() {
            return this.getAttribute('data-base-optionvalue');
        }

        connectedCallback() {
            KikVariantCompareCard._instances.add(this);
            KikVariantCompareCard._initObserver();

            // Validate on initial load using the current variant from the picker
            const picker = document.querySelector('variant-picker');
            if (picker) {
                const jsonEl = picker.querySelector('[data-selected-variant-json]');
                if (jsonEl) {
                    try {
                        let variant = JSON.parse(jsonEl.textContent);
                        if (!variant) {
                            const optionGroups = picker.querySelectorAll('[data-variant-picker-option-inputs]');
                            const selectedOptions = [];
                            optionGroups.forEach((group) => {
                                const checked = group.querySelector('input:checked');
                                if (checked) selectedOptions.push(checked.value);
                            });
                            if (selectedOptions.length > 0) {
                                variant = { title: selectedOptions.join(' / ') };
                            }
                        }
                        // console.log('[kik-variant-compare-card] Initial load update:', variant);
                        this._updateCard(variant);
                    } catch (e) { /* ignore */ }
                }
            }
        }

        disconnectedCallback() {
            KikVariantCompareCard._instances.delete(this);
            if (KikVariantCompareCard._instances.size === 0 && KikVariantCompareCard._observer) {
                KikVariantCompareCard._observer.disconnect();
                KikVariantCompareCard._observer = null;
                // console.log('[kik-variant-compare-card] Observer disconnected — no instances left');
            }
        }

        // --- Static: single shared MutationObserver for all card instances ---
        static _instances = new Set();
        static _observer = null;
        static _lastVariantId = null;

        static _initObserver() {
            if (this._observer) return;

            // Track the initial variant so we only react to changes
            const initialPicker = document.querySelector('variant-picker');
            if (initialPicker) {
                const jsonEl = initialPicker.querySelector('[data-selected-variant-json]');
                if (jsonEl) {
                    try {
                        const v = JSON.parse(jsonEl.textContent);
                        this._lastVariantId = v.id;
                        // console.log('[kik-variant-compare-card] Initial variant id:', v.id);
                    } catch (e) { /* ignore */ }
                }
            }

            this._observer = new MutationObserver((mutations) => {
                for (const mutation of mutations) {
                    for (const node of mutation.addedNodes) {
                        if (node.nodeType !== Node.ELEMENT_NODE) continue;

                        // The theme replaces [data-product-block] elements.
                        // One of them contains the new variant-picker.
                        const picker = node.matches?.('variant-picker')
                            ? node
                            : node.querySelector?.('variant-picker');

                        if (!picker) continue;

                        const jsonEl = picker.querySelector('[data-selected-variant-json]');
                        if (!jsonEl) continue;

                        try {
                            let variant = JSON.parse(jsonEl.textContent);
                            // console.log('[kik-variant-compare-card] Detected variant swap:', variant);

                            // When combination is unavailable, variant is null.
                            // Read selected options from the picker inputs to build a synthetic title
                            // so each card can still resolve its own combination.
                            if (!variant) {
                                const optionGroups = picker.querySelectorAll('[data-variant-picker-option-inputs]');
                                const selectedOptions = [];
                                optionGroups.forEach((group) => {
                                    const checked = group.querySelector('input:checked');
                                    if (checked) selectedOptions.push(checked.value);
                                });
                                // console.log('[kik-variant-compare-card] Unavailable combo, selected options:', selectedOptions);
                                if (selectedOptions.length > 0) {
                                    variant = { title: selectedOptions.join(' / ') };
                                }
                            }

                            this._instances.forEach((card) => card._updateCard(variant));
                        } catch (e) {
                            // console.error('[kik-variant-compare-card] Error parsing variant JSON:', e);
                        }
                        return;
                    }
                }
            });

            // Observe the product section specifically if possible, else fallback to body
            const productSection = initialPicker?.closest('.shopify-section') || document.body;
            this._observer.observe(productSection, { childList: true, subtree: true });
            // console.log('[kik-variant-compare-card] Observer started on:', productSection.tagName, productSection.id || '');
        }

        // --- Helpers ---

        /** Replicate Shopify's `| handle` filter in JS */
        static handleize(str) {
            return str
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');
        }

        /**
         * Shared fetch cache so all card instances reuse a single request
         * per variant (keyed by variant ID).
         */
        static _fetchCache = { key: null, promise: null };

        static fetchSection(variant) {
            const cacheKey = variant?.id || 'no-variant';
            if (this._fetchCache.key === cacheKey && this._fetchCache.promise) {
                return this._fetchCache.promise;
            }

            const picker = document.querySelector('variant-picker');
            const productUrl = picker?.dataset?.url || window.location.pathname;
            const url = variant?.id
                ? `${productUrl}?variant=${variant.id}&sections=kik-variants-compare-fetch`
                : `${productUrl}?sections=kik-variants-compare-fetch`;

            // console.log('[kik-variant-compare-card] Fetch URL:', url);

            this._fetchCache.key = cacheKey;
            this._fetchCache.promise = fetch(url)
                .then((res) => res.json())
                .then((data) => {
                    const html = data['kik-variants-compare-fetch'];
                    const parser = new DOMParser();
                    return parser.parseFromString(html, 'text/html');
                });

            return this._fetchCache.promise;
        }

        /**
         * Trigger Yotpo Widgets v3 (cdn-widgetsrepository) to scan for newly-inserted
         * .yotpo-widget-instance elements after we swap the card DOM via the Section
         * Rendering API. Without this, Yotpo's initial scan misses cards that arrive
         * later and they render as empty widgets (no stars).
         *
         * Multi-fallback because the loader exposes different APIs across versions
         * and the script is async — at worst this is a quiet no-op.
         */
        static _rehydrateYotpoWidgets() {
            try {
                const container = window.yotpoWidgetsContainer;
                if (container && container.guard && typeof container.guard.checkForElements === 'function') {
                    container.guard.checkForElements();
                } else if (container && typeof container.initWidgets === 'function') {
                    container.initWidgets();
                } else if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') {
                    window.yotpo.refreshWidgets();
                }
            } catch (e) {
                // Loader may not be ready yet — silently ignore
            }
        }

        _showPreloader() {
            const preloader = this.querySelector('.kik-js-preloader');
            if (preloader) preloader.classList.remove('hidden');
        }

        _hidePreloader() {
            const preloader = this.querySelector('.kik-js-preloader');
            if (preloader) preloader.classList.add('hidden');
        }

        /** Sync slider-grid__slide class with inner kik-variant-card--unavailable */
        _syncSlideUnavailableClass() {
            const slide = this.closest('.slider-grid__slide');
            const card = this.querySelector('.kik-variant-card');
            if (!slide || !card) return;
            if (card.classList.contains('kik-variant-card--unavailable')) {
                slide.classList.add('kik-variant-card--unavailable');
            } else {
                slide.classList.remove('kik-variant-card--unavailable');
            }
        }

        // --- Card Update ---
        async _updateCard(variant) {
            // console.log("variant: ", variant);
            this._showPreloader();

            // If variant is null (unavailable combination), show unavailable card
            if (!variant || !variant.title) {
                const doc = await KikVariantCompareCard.fetchSection(variant);
                const unavailableCard = doc.querySelector(
                    '.kik-variant-card[data-variant-title="unavailable"]'
                );
                if (unavailableCard) {
                    const currentCard = this.querySelector('.kik-variant-card');
                    if (currentCard) {
                        currentCard.replaceWith(unavailableCard.cloneNode(true));
                        KikVariantCompareCard._rehydrateYotpoWidgets();
                    }
                }
                this._syncSlideUnavailableClass();
                this._hidePreloader();
                return;
            }

            // 1. Get option values from the selected variant title e.g. "Queen / 10 / Firm"
            const optionValues = variant.title.split(' / ');

            // 2. Build target variant title by swapping the compare-option
            //    position with this card's base option value
            const targetOptions = [...optionValues];
            targetOptions[this.baseOptionPos - 1] = this.baseOptionValue;
            const targetTitle = targetOptions.join(' / ');
            const targetHandle = KikVariantCompareCard.handleize(targetTitle);

            // console.log("targetTitle: ", targetTitle);
            // console.log("targetHandle: ", targetHandle);

            // 3. Fetch all variant cards via the Section Rendering API
            const doc = await KikVariantCompareCard.fetchSection(variant);

            // 4. Find the card that matches the target variant title,
            //    fall back to the "unavailable" card if not found
            let newCard = doc.querySelector(
                `.kik-variant-card[data-variant-title="${targetHandle}"]`
            );
            if (!newCard) {
                newCard = doc.querySelector(
                    '.kik-variant-card[data-variant-title="unavailable"]'
                );
            }

            // console.log("newCard found: ", !!newCard);

            if (newCard) {
                const cloned = newCard.cloneNode(true);

                // Update badge value with this card's compare option value
                const badgeEl = cloned.querySelector('.kik-js-badge-value');
                if (badgeEl) badgeEl.textContent = this.baseOptionValue;

                // Build price label: variant title without the badge value, appended with ":"
                // e.g. "X / 12 / A" with badge "12" → "X / A:"
                const priceLabel = targetOptions
                    .filter((_, i) => i !== this.baseOptionPos - 1)
                    .join(' / ') + ':';
                const priceLabelEl = cloned.querySelector('.kik-js-price-label');
                if (priceLabelEl) priceLabelEl.textContent = priceLabel;

                const currentCard = this.querySelector('.kik-variant-card');
                if (currentCard) {
                    currentCard.replaceWith(cloned);
                    KikVariantCompareCard._rehydrateYotpoWidgets();
                }
            }
            this._syncSlideUnavailableClass();
            this._hidePreloader();
        }
    }
    customElements.define('kik-variant-compare-card', KikVariantCompareCard);
}

if (!customElements.get('kik-variants-compare')) {
    class KikVariantsCompare extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback() {
        }

        disconnectedCallback() {
        }
    }
    customElements.define('kik-variants-compare', KikVariantsCompare);
}
